<script>
    import Fuse from "fuse.js";
    import queryString from "query-string";
    import { fade } from "svelte/transition";
    import { flip } from "svelte/animate";
    import Card from "./Card.svelte";
    import ResultCount from "./ResultCount.svelte";
    import ResetButton from "./ResetButton.svelte";
    import FilterTags from "./FilterTags.svelte";
    import SearchField from "./SearchField.svelte";

    // Get query params
    const queryStringOptions = { arrayFormat: "bracket-separator" };
    const initialValues = queryString.parse(
        window.location.search,
        queryStringOptions
    );

    // Get resources
    const resources = window.resourcesMetadata.resources.map((item) => {
        let linkLabel = "View";
        if (item.type === "externalLink") {
            try {
                const url = new URL(item.url);
                linkLabel = url.hostname.replace(/^www\./i, "");
            } catch (e) {
                console.error(e);
            }
        } else if (item.type === "download") {
            linkLabel = "Download";
        }
        return {
            ...item,
            linkLabel,
        };
    });

    // Configure keyword search
    let searchTerm = initialValues.searchTerm || "";
    const fuse = new Fuse(resources, {
        threshold: 0.3,
        keys: [
            {
                name: "title",
                weight: 2,
            },
            {
                name: "description",
                weight: 1.5,
            },
            {
                name: "tags",
                weight: 1,
            },
        ],
    });

    // Debounce search (animation bug)
    let searchTermTimer;
    let searchStatus = "searchIdle";
    const setSearchTerm = (v) => {
        searchStatus = "searchWaiting";
        clearTimeout(searchTermTimer);
        searchTermTimer = setTimeout(() => {
            searchTerm = v;
            searchStatus = "searchDone";
        }, 500);
    };

    // Extract tags
    const filterTags = window.resourcesMetadata.filterTags;

    // Apply filters
    let isFiltered = false;
    let filteredResources = [];
    let selectedTags = initialValues.selectedTags || [];
    let collectionTag = initialValues.collection || "";
    let tagCounts = {};
    $: {
        // Reset pagination whenever list changes
        visibleResources = resourceLimit;

        // Keyword search
        filteredResources = searchTerm
            ? fuse.search(searchTerm).map((fuseItem) => fuseItem.item)
            : resources;

        // Tags
        tagCounts = {};
        filterTags.forEach(({ tags }) => {
            // Count tags in each section before tags in that section are applied
            tags.forEach((tag) => {
                tagCounts[tag] = filteredResources.filter((resource) =>
                    resource.tags.includes(tag)
                ).length;
            });
            const selectedSectionTags = selectedTags.filter((tag) =>
                tags.includes(tag)
            );
            filteredResources = selectedSectionTags.length
                ? filteredResources.filter((resource) =>
                      resource.tags.some((tag) =>
                          selectedSectionTags.includes(tag)
                      )
                  )
                : filteredResources;
        });

        // Collection
        filteredResources = collectionTag
            ? filteredResources.filter((resource) =>
                  resource.tags.includes(collectionTag)
              )
            : filteredResources;

        // Are any filters in play?
        isFiltered = searchTerm || collectionTag || selectedTags.length;

        // Update query params whenever list changes
        const newLocation = new URL(window.location.href);
        newLocation.search = queryString.stringify(
            {
                searchTerm: searchTerm || undefined,
                selectedTags: selectedTags.length ? selectedTags : undefined,
                collection: collectionTag || undefined,
            },
            queryStringOptions
        );
        history.replaceState(null, "", newLocation);
    }

    // Setup pagination
    const resourceLimit = 20;
    $: visibleResources = Math.min(resourceLimit, filteredResources.length);

    // Reset the filter
    function reset() {
        searchTerm = "";
        collectionTag = "";
        selectedTags = [];
    }

    // Responsive rendering
    let showFilters = false;
    let windowWidth = 0;
    $: smallScreen = windowWidth < 1000;
</script>

<svelte:window bind:innerWidth={windowWidth} />

{#if collectionTag}
    <div class="resourcesHeader">
        <h2>Showing resources about <i>“{collectionTag}”</i></h2>
        <p class="resultCount">
            <ResultCount {filteredResources}
                >.
                <button class="link" on:click={reset}>
                    View all resources
                </button></ResultCount
            >
        </p>
    </div>
{/if}

<div
    class={`resourcesCollection ${searchStatus}`}
    class:collection={collectionTag}
>
    {#if !collectionTag}
        {#if smallScreen}
            <div class="mobileFilterActions">
                <button
                    class="showFilters"
                    on:click={() => {
                        showFilters = true;
                    }}
                    >Search and Filter Resources <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                            clip-rule="evenodd"
                        />
                    </svg></button
                >
                <ResultCount {filteredResources}
                    >{#if isFiltered}. <button class="link" on:click={reset}
                            >Reset filters</button
                        >{/if}
                </ResultCount>
            </div>
            <div class="mobileFilterContainer" class:visible={showFilters}>
                <div
                    class="overlay"
                    on:click={() => {
                        showFilters = false;
                    }}
                />
                <div class="filters">
                    <SearchField {setSearchTerm} {searchTerm} />
                    <FilterTags {tagCounts} {filterTags} bind:selectedTags />
                    <div class="actionContainer">
                        <ResultCount {filteredResources} />
                        <ResetButton {reset} {isFiltered} />
                        <button
                            class="appButton closeFilters"
                            on:click={() => {
                                showFilters = false;
                            }}>{isFiltered ? "Apply" : "Close"}</button
                        >
                    </div>
                </div>
            </div>
        {:else}
            <div class="desktopFilterContainer">
                <div class="filters">
                    <SearchField {setSearchTerm} {searchTerm} />
                    <ResetButton {reset} {isFiltered} />
                    <ResultCount {filteredResources} />
                    <FilterTags {tagCounts} {filterTags} bind:selectedTags />
                </div>
            </div>
        {/if}
    {/if}

    <div class="resourceContainer">
        <div class="ResourceList">
            {#each filteredResources.slice(0, visibleResources) as resource (resource.id)}
                <article
                    transition:fade|local={{ duration: 300 }}
                    animate:flip={{ duration: 300 }}
                >
                    <Card {resource} />
                </article>
            {/each}
        </div>
        <div class="pagination">
            <p class="resultCount">
                Showing {visibleResources} of {filteredResources.length} results
            </p>
            {#if visibleResources < filteredResources.length}
                <div class="loadMore">
                    <button
                        class="button"
                        on:click={() => {
                            visibleResources = Math.min(
                                visibleResources + resourceLimit,
                                filteredResources.length
                            );
                        }}>Show more</button
                    >
                </div>
            {/if}
        </div>
    </div>
</div>
